import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Assignee from 'models/assignee';
import Attachments from '../attachments/attachments';
import BubbleContent from '../content/bubble_content';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ErrorBoundary from 'components/common/utilities/error_boundary';
import FakeCommentEditor from 'components/customer/task_comments/fake_comment_editor';
import { getIsCommentingEnabledForTask } from 'scripts/application/selectors/customer';
import ItemContent from '../item_content';
import { ACTOR_PHRASES } from '../metadata/actor_sentence';
import ItemMetadata from '../item_metadata';
import Linkifier from 'components/lib/linkifier';
import OpenOrRefreshCommentPanel from 'actions/task/open_or_refresh_comment_panel';
import SafeHtml from 'components/common/safe_html';
import TaskAnalyticsLocations from 'actions/conversation_item/task/task_analytics_locations';
import TaskItemCommentPreview from './task_item_comment_preview';
import TaskHeader from './task_header';
import TaskThread from 'components/customer/task_comments/task_thread';
import TaskTopicsPills from './task_topics_pills';
import UpdateTaskAssignee from 'actions/conversation_item/task/update_task_assignee';
import { useExecuteAction } from 'components/hooks/connect_hooks';

// The feed card shows the tail of a thread; the panel has the rest.
const FEED_THREAD_LIMIT = 5;

export function CommentableTaskContent({
  className,
  comment,
  commentCount,
  thread = [],
  isCommentingEnabled,
  hasConversationGeneral,
  isTopicsOnTasksEnabled,
  isPending,
  item,
}) {
  const executeAction = useExecuteAction();
  const { t } = useTranslation();
  const task = item.content;
  const isLink = item.isLink();

  const onOpenCommentPanel = useCallback(
    (location, e) => {
      e.stopPropagation();
      executeAction(OpenOrRefreshCommentPanel, {
        itemId: item.id,
        location,
      });
    },
    [executeAction, item.id]
  );

  const onClickComment = useCallback(
    e => onOpenCommentPanel(TaskAnalyticsLocations.TASK_COMMENT, e),
    [onOpenCommentPanel]
  );
  const onClickCommentEditor = useCallback(
    e => onOpenCommentPanel(TaskAnalyticsLocations.FAKE_COMMENT_EDITOR, e),
    [onOpenCommentPanel]
  );

  const onChangeAssignee = useCallback(
    ({ agentId, routingGroupId }) =>
      executeAction(UpdateTaskAssignee, {
        assignee: new Assignee({ agentId: agentId || '', routingGroupId }),
        conversationItemId: item.id,
        location: TaskAnalyticsLocations.TASK_ITEM,
      }),
    [executeAction, item.id]
  );

  function render() {
    return (
      <StyledBubbleContent className={className} data-aid="commentableTask" item={item}>
        <TaskHeader
          hasConversationGeneral={hasConversationGeneral}
          isLink={isLink}
          isPending={isPending}
          item={item}
          location={TaskAnalyticsLocations.TASK_ITEM}
          onChangeAssignee={onChangeAssignee}
        />
        {renderText()}
        {renderAttachments()}
        {renderThread()}
        {renderCommentEditor()}
        {renderTopics()}
      </StyledBubbleContent>
    );
  }

  // A task with comments but no thread emails keeps its preview, so enabling the flag doesn't turn
  // every card into a wall of comments.
  function renderThread() {
    if (!hasThreadEmails()) {
      return <TaskItemCommentPreview comment={comment} commentCount={commentCount} onClick={onClickComment} />;
    }

    const visibleThread = thread.slice(-FEED_THREAD_LIMIT);
    const hiddenCount = thread.length - visibleThread.length;

    return (
      <div>
        <TaskThread inItemDisplay thread={visibleThread} />
        {renderThreadLink(hiddenCount)}
      </div>
    );
  }

  // Emails and comments are read in place; the panel is one deliberate click away. The link is the
  // only way in once the task is closed, since the reply editor is gone then.
  function renderThreadLink(hiddenCount) {
    if (hiddenCount > 0) {
      return (
        <StyledThreadLink data-aid="taskItem-viewThread" onClick={onClickComment}>
          {t('View all ({{count}})', { count: thread.length })}
        </StyledThreadLink>
      );
    }
    if (!isCommentingEnabled) {
      return (
        <StyledThreadLink data-aid="taskItem-viewThread" onClick={onClickComment}>
          {t('View thread')}
        </StyledThreadLink>
      );
    }
    return null;
  }

  function renderTopics() {
    if (!hasConversationGeneral || !isTopicsOnTasksEnabled) {
      return null;
    }
    // A closed task offers no "Add topic", so with nothing applied there is nothing to show.
    const isClosed = !!(task.isClosed && task.isClosed());
    if (isClosed && _.isEmpty(task.topicIds) && _.isEmpty(task.customAttributes)) {
      return null;
    }
    return (
      <StyledTopics>
        <ErrorBoundary errorMeta={{ taskId: item.id }} renderError={() => null}>
          <TaskTopicsPills className="taskItem-topics" taskId={item.id} />
        </ErrorBoundary>
      </StyledTopics>
    );
  }

  function renderCommentEditor() {
    return isCommentingEnabled && <FakeCommentEditor hasThread={hasThreadEmails()} onClick={onClickCommentEditor} />;
  }

  function hasThreadEmails() {
    return thread.some(entry => entry.kind === 'email');
  }

  function renderText() {
    const { bodyHtml, note } = task;

    if (bodyHtml) {
      return <SafeHtml className="taskItem-text" dir="auto" html={bodyHtml} setDirection />;
    } else if (note) {
      return <Linkifier>{note}</Linkifier>;
    }

    return null;
  }

  function renderAttachments() {
    return (
      <Attachments
        attachments={task.attachments || []}
        className="taskItem-attachments"
        itemId={item.id}
        pathPrefix="task"
      />
    );
  }

  return render();
}

CommentableTaskContent.propTypes = {
  comment: PropTypes.object,
  commentCount: PropTypes.number,
  thread: PropTypes.array,
  isCommentingEnabled: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

const StyledBubbleContent = styled(BubbleContent)`
  border-radius: 10px;
`;

const StyledThreadLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.green400};
  cursor: pointer;
  font-size: 12px;
  margin: 4px 0 8px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledTopics = styled.div`
  /* Match the description-to-comment-editor gap (.taskItem-text margin-bottom).
     No bottom margin — the topics are the last thing in the bubble. */
  margin: 8px 0 0;

  /* The shared topic multi-select stretches to fill / center within the
     conversation bottom bar; in the task bubble we want it compact, full-width,
     and left-aligned under the header. */
  .conversationTopic-multi {
    height: auto;
    justify-content: flex-start;
    max-width: 100%;
  }

  .conversationTopic-menuOpener {
    padding-left: 0;
  }
`;

function mapStateToProps({ getProvider, isFeatureEnabled }, { item }) {
  const taskInfoProvider = getProvider('taskInfo');
  const conversationHistoryProvider = getProvider('conversationHistory');
  let taskInfo = taskInfoProvider.findBy({ id: item.id });
  let comments = taskInfo ? taskInfo.getComments() : [];
  let thread = taskInfo && isFeatureEnabled('taskSideThreads') ? taskInfo.getThread() : [];

  const hasConversationGeneral = isFeatureEnabled('conversation:general');

  return {
    comment: _.maxBy(comments, 'createdAt'),
    commentCount: comments.length,
    thread,
    isPending: conversationHistoryProvider.has({ id: item.id }) && conversationHistoryProvider.isPending(item.id),
    item,
    isCommentingEnabled:
      hasConversationGeneral &&
      getIsCommentingEnabledForTask(
        { taskInfo: taskInfoProvider, conversationHistory: conversationHistoryProvider },
        { taskId: item.id }
      ),
    hasConversationGeneral,
    isTopicsOnTasksEnabled: isFeatureEnabled('topicsOnTasks'),
  };
}

const CommentableTaskContentContainer = connect(mapStateToProps)(CommentableTaskContent);
CommentableTaskContentContainer.propTypes = CommentableTaskItem.propTypes;
export { CommentableTaskContentContainer };

function mapItemStateToProps({ getProvider }, { item }) {
  const commentPanel = getProvider('commentPanel').get();
  let isFocused = commentPanel.selectedItemId === item.id;
  return { isFocused };
}

export function CommentableTaskItem({ className, item, isFocused }) {
  return (
    <ItemContent isHighlighted={isFocused} item={item}>
      <ItemMetadata item={item} phrase={ACTOR_PHRASES.CREATED_TASK} />
      <CommentableTaskContentContainer className={className} item={item} />
    </ItemContent>
  );
}

const CommentableTaskItemContainer = connect(mapItemStateToProps)(CommentableTaskItem);
export default CommentableTaskItemContainer;

CommentableTaskItem.propTypes = {
  className: PropTypes.string,
  conversationId: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  isCommentPanelOpen: PropTypes.bool,
  isFocused: PropTypes.bool,
};
